import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaArrowLeft, FaGlobe, FaTimes } from "react-icons/fa";
import { useLanguage } from "../../components/LanguageContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Language,
  Category,
  SubCategory,
  FullPackageData,
  PackageRequest,
} from "../../Types/types";
import { addpackagetranslation } from "./addpackagetranslation";
import {
  RootState,
  AppDispatch,
} from "../../components/Manual_List/fetch/store";
import {
  fetchFullPackageById,
  fetchCategories,
  fetchSubCategories,
  postPackage,
  uploadPackageThumbnail,
  patchPackage,
  fetchThumbnail,
  deletePackageThumbnail,
} from "../../components/Manual_List/fetch/slices";
import { getSubCategoriesForCategory } from "../../components/Mappers/subcategoryToCategory";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { AddLanguageModal } from "../../components/shared/AddLanguageModal";

const AddPackage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { language } = useLanguage();
  const t = addpackagetranslation[language as Language];
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const categoryData = useSelector(
    (state: RootState) => state.data.categories.data
  ) as Category[];
  const subCategoryData = useSelector(
    (state: RootState) => state.data.subcategories.data
  ) as SubCategory[];
  const { state } = location;

  const [names, setNames] = useState<Record<string, string>>(
    state?.names || {}
  );
  const [descriptions, setDescriptions] = useState<Record<string, string>>(
    state?.descriptions || {}
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    state?.selectedCategory || ""
  );
  const [subcategories, setSubcategories] = useState<string>(
    state?.subcategories || ""
  );
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    SubCategory[]
  >([]);
  const [thumbnail, setThumbnail] = useState<File | null>(
    state?.thumbnail || null
  );
  const [dragActive, setDragActive] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [newPackageId, setNewPackageId] = useState<string | null>(null);

  const fetchPackageData = useCallback(async () => {
    if (id) {
      const result = await dispatch(fetchFullPackageById({ id, language }));
      if (result.payload) {
        const packageData = result.payload as FullPackageData;
        setNames(packageData.label);
        setDescriptions(packageData.description);
        setSelectedCategory(packageData.subCategory.categoryId.toString());
        setSubcategories(packageData.subCategory.id.toString());

        const resultThumbnail = await dispatch(fetchThumbnail({ id }));
        if (fetchThumbnail.fulfilled.match(resultThumbnail)) {
          let { thumbnailUrl } = resultThumbnail.payload;

          if (thumbnailUrl.startsWith("data:image/png;base64,")) {
            thumbnailUrl = thumbnailUrl.replace("data:image/png;base64,", "");
          }

          setThumbnailUrl(thumbnailUrl);
        }
      }
    }
  }, [id, dispatch, language]);

  useEffect(() => {
    fetchPackageData();
    dispatch(fetchCategories(language) as any);
    dispatch(fetchSubCategories(language) as any);
  }, [id, dispatch, language, fetchPackageData]);

  useEffect(() => {
    if (selectedCategory) {
      const categoryName = categoryData.find(
        (category) => category.id.toString() === selectedCategory
      )?.name;

      if (categoryName) {
        const filtered = getSubCategoriesForCategory(
          subCategoryData,
          categoryName
        );
        setFilteredSubCategories(filtered);
      }
    } else {
      setFilteredSubCategories([]);
    }
  }, [selectedCategory, subCategoryData, categoryData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setThumbnail(e.target.files[0]);
    }
  };

  const handleRemoveThumbnail = async () => {
    setThumbnail(null);
    setThumbnailUrl(null);
    await dispatch(deletePackageThumbnail(Number(id)));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setThumbnail(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const packageData: PackageRequest = {
      subCategoryId: parseInt(subcategories),
      label: names,
      description: descriptions,
    };

    let packageId: string | undefined = id;

    if (id) {
      const resultAction = await dispatch(patchPackage({ id, packageData }));

      if (patchPackage.fulfilled.match(resultAction)) {
        packageId = id;
        toast.success(t.packageEditedSuccess);
      }
    } else {
      const resultAction = await dispatch(postPackage(packageData));

      if (postPackage.fulfilled.match(resultAction)) {
        const newPackage = resultAction.payload;
        packageId = newPackage.id?.toString();
        if (packageId) {
          toast.success(t.packageSuccess);
          setNewPackageId(packageId);
        }
      }
    }

    if (thumbnail && packageId) {
      await dispatch(
        uploadPackageThumbnail({ id: parseInt(packageId), thumbnail })
      );
    }
  };

  const handleNavigateToSteps = () => {
    const effectiveId = id || newPackageId;

    if (effectiveId) {
      dispatch(fetchFullPackageById({ id: effectiveId, language })).then(
        (result) => {
          if (result.payload) {
            const packageData = result.payload as FullPackageData;

            navigate(`/steps-list/${effectiveId}`, {
              state: {
                id: effectiveId,
                names,
                descriptions,
                selectedCategory,
                subcategories,
                thumbnail,
                steps: packageData.packageContents,
              },
            });
          }
        }
      );
    } else {
      navigate("/steps-list", {
        state: {
          names,
          descriptions,
          selectedCategory,
          subcategories,
          thumbnail,
        },
      });
    }
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <ToastContainer />
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow pb-20">
        <form
          id="add-package-form"
          className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center mb-6">
            <FaArrowLeft
              className="text-gray-600 dark:text-gray-400 cursor-pointer mr-3 hover:text-purple dark:hover:text-purple transition-colors"
              onClick={() => navigate("/packages-list/")}
            />
            <h1 className="text-2xl font-semibold">{t.title1}</h1>
          </div>
          <div className="mb-6 flex justify-start space-x-3">
            <button
              type="button"
              className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
            >
              {t.details}
            </button>
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm dark:bg-white dark:text-black hover:bg-opacity-90 transition-all"
              onClick={handleNavigateToSteps}
            >
              {t.steps}
            </button>
          </div>
          <div className="mb-4 relative">
            <label
              htmlFor="name"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.name}
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                className="w-full pl-4 pr-12 py-3 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple dark:bg-darkBackground dark:border-gray-600 dark:focus:border-purple transition-colors cursor-pointer"
                value={names[language] || ""}
                onChange={(e) =>
                  setNames({ ...names, [language]: e.target.value })
                }
                onClick={() => setIsNameModalOpen(true)}
                autoComplete="off"
                placeholder="Enter package name..."
              />
              <FaGlobe className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="mb-4 relative">
            <label
              htmlFor="description"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.description}
            </label>
            <div className="relative">
              <input
                id="description"
                className="w-full pl-4 pr-12 py-3 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple dark:bg-darkBackground dark:border-gray-600 dark:focus:border-purple transition-colors cursor-pointer"
                value={descriptions[language] || ""}
                onChange={(e) =>
                  setDescriptions({
                    ...descriptions,
                    [language]: e.target.value,
                  })
                }
                onClick={() => setIsDescriptionModalOpen(true)}
                autoComplete="off"
                placeholder="Enter package description..."
              />
              <FaGlobe className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="mb-4">
            <label
              htmlFor="category"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.category}
            </label>
            <select
              id="category"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple dark:border-gray-600 dark:focus:border-purple transition-colors appearance-none bg-white dark:bg-darkBackground"
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSubcategories("");
              }}
            >
              <option value="" disabled>
                Select a category...
              </option>
              {Array.isArray(categoryData) &&
                categoryData.map((category: Category) => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="mb-4">
            <label
              htmlFor="subcategory"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.subcategory}
            </label>
            <select
              id="subcategory"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple dark:border-gray-600 dark:focus:border-purple transition-colors appearance-none bg-white dark:bg-darkBackground disabled:opacity-50 disabled:cursor-not-allowed"
              value={subcategories}
              onChange={(e) => setSubcategories(e.target.value)}
              disabled={!selectedCategory}
            >
              <option value="" disabled>
                Select a subcategory...
              </option>
              {Array.isArray(filteredSubCategories) &&
                filteredSubCategories.map((subCategory: SubCategory) => (
                  <option
                    key={subCategory.id}
                    value={subCategory.id.toString()}
                  >
                    {subCategory.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="mb-6">
            <label
              htmlFor="thumbnail"
              className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t.thumbnail}
            </label>
            <div
              className={`w-full p-8 border-2 border-dashed rounded-lg shadow-sm dark:bg-darkBackground transition-all ${
                dragActive
                  ? "border-purple bg-purple bg-opacity-5 dark:bg-purple dark:bg-opacity-10"
                  : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="thumbnail"
                className="hidden"
                onChange={handleFileChange}
                accept="image/*"
              />
              <label
                htmlFor="thumbnail"
                className="w-full h-full flex flex-col justify-center items-center cursor-pointer relative min-h-[160px]"
              >
                {thumbnailUrl ? (
                  <div className="relative">
                    <img
                      src={thumbnailUrl}
                      alt="Thumbnail"
                      className="max-h-48 rounded-lg shadow-md"
                    />
                    {(thumbnailUrl || thumbnail) && (
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-white dark:bg-darkSecondaryBackground rounded-full p-2 shadow-lg hover:bg-red-50 dark:hover:bg-red-900 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveThumbnail();
                        }}
                      >
                        <FaTimes className="text-red-500 text-sm" />
                      </button>
                    )}
                  </div>
                ) : thumbnail ? (
                  <div className="relative text-center">
                    <div className="bg-purple bg-opacity-10 dark:bg-purple dark:bg-opacity-20 rounded-lg px-6 py-4">
                      <p className="font-medium text-purple">
                        {thumbnail.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Click to change or drag a new file
                      </p>
                    </div>
                    {(thumbnailUrl || thumbnail) && (
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-white dark:bg-darkSecondaryBackground rounded-full p-2 shadow-lg hover:bg-red-50 dark:hover:bg-red-900 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveThumbnail();
                        }}
                      >
                        <FaTimes className="text-red-500 text-sm" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 font-medium">
                      {t.thumbnailDrop}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-8 pt-6">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm dark:bg-white dark:text-black hover:bg-opacity-90 transition-all"
              onClick={() => navigate(-1)}
            >
              {t.discard}
            </button>
            <button
              type="submit"
              className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              form="add-package-form"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
      <AddLanguageModal
        isOpen={isNameModalOpen}
        onClose={() => setIsNameModalOpen(false)}
        translations={t}
        mode="name"
        values={names}
        setValues={setNames}
      />
      <AddLanguageModal
        isOpen={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        translations={t}
        mode="description"
        values={descriptions}
        setValues={setDescriptions}
      />
    </div>
  );
};

export default AddPackage;
